import type { ProjectNote, ProjectNoteKind } from "../assistant";
import { projectNoteKindLabel } from "../assistant";
import type { CreateProjectRequirementTextDraft } from "./createDraft";
import { slug } from "./createDraft";

export function promotedRequirementType(note: ProjectNote): string {
  if (note.kind === "exclusion") {
    return "scope_exclusion";
  }

  if (note.kind === "commissioning") {
    return "commissioning_requirement";
  }

  if (note.kind === "clarification") {
    return "clarification_requirement";
  }

  return "requirement_amendment";
}

export function promotedRequirementId(note: ProjectNote): string {
  return note.promotedRequirementId ?? `requirement-note-${slug(note.id)}`;
}

export function sourceNoteLabel(note: ProjectNote): string {
  const author = (note.authorType ?? "user") === "ai" ? "AI" : "User";

  return `${author} ${projectNoteKindLabel(note.kind).toLowerCase()} note`;
}

const PROMOTED_REQUIREMENT_FRAMING: Record<ProjectNoteKind, string> = {
  exclusion: "Scope exclusion carried from the project notes:",
  commissioning: "Commissioning requirement carried from the project notes:",
  clarification: "Clarification requirement carried from the project notes:",
  "requirement-amendment": "Requirement amendment carried from the project notes:"
};

export function humaniseNoteTextForRequirement(text: string): string {
  const collapsed = text.replace(/\s+/g, " ").trim();

  if (!collapsed) {
    return "";
  }

  const capitalised = `${collapsed.charAt(0).toUpperCase()}${collapsed.slice(1)}`;

  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

export function promotedRequirementText(note: ProjectNote): string {
  const framing = PROMOTED_REQUIREMENT_FRAMING[note.kind];
  const detail = humaniseNoteTextForRequirement(note.text);

  if (!detail) {
    return `${framing} the promoted note did not include reviewable text and must be completed before client issue.`;
  }

  return `${framing} ${detail}`;
}

export function promotedRequirementDraft(
  note: ProjectNote,
  id = promotedRequirementId(note)
): CreateProjectRequirementTextDraft {
  return {
    id,
    type: promotedRequirementType(note),
    interpretedText: promotedRequirementText(note),
    priority: "default",
    confidence: (note.authorType ?? "user") === "ai" ? "0.72" : "0.8",
    scopeLabel: note.subjectRef?.label ?? "Project notes",
    provenance: "manual",
    sourceNoteId: note.id,
    sourceNoteLabel: sourceNoteLabel(note),
    sourceNoteAuthorType: note.authorType ?? "user"
  };
}

export function noteIsAppliedToRequirements(note: ProjectNote): boolean {
  return note.status === "promoted_to_requirement" || Boolean(note.promotedRequirementId);
}
